/**
 * TagUnspecifiedDialog — admin tool to retroactively assign a payment/payout
 * method to legacy transactions that were recorded before payment-method
 * tracking rolled out. Lists untagged deposit-like or cashout-like txns and
 * lets the admin tag them one-by-one or in bulk.
 *
 * Used by the Admin platform-wide "Today's Loads/Cashouts by Method" card
 * when the user clicks the "Unspecified" tile.
 */
import { useEffect, useState, useCallback } from 'react';import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Tag, CheckCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const METHODS = ['Cash', 'Cash App', 'Chime', 'PayPal', 'Venmo', 'Zelle', 'Apple Pay', 'Other'];

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtWhen = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

const TagUnspecifiedDialog = ({ open, onOpenChange, kind = 'load', onDone }) => {
    const [rows, setRows] = useState([]);
    const [days, setDays] = useState(7);
    const [loading, setLoading] = useState(false);
    const [bulkMethod, setBulkMethod] = useState('');
    const [bulkBusy, setBulkBusy] = useState(false);
    const [rowBusy, setRowBusy] = useState({}); // {txnId: true}

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${API}/admin/untagged-loads`, {
                    params: { kind, days, limit: 200 },
                    withCredentials: true,
                });
                if (!cancelled) setRows(data.rows || []);
            } catch (e) {
                if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load untagged transactions');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, kind, days]);

    const tagOne = useCallback(async (txnId, method) => {
        setRowBusy(s => ({ ...s, [txnId]: true }));
        try {
            await axios.patch(`${API}/admin/transactions/${txnId}/method`,
                { method }, { withCredentials: true });
            setRows(prev => prev.filter(r => r.id !== txnId));
            toast.success(`Tagged as ${method}`);
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to tag');
        } finally {
            setRowBusy(s => { const n = { ...s }; delete n[txnId]; return n; });
        }
    }, [onDone]);

    const bulkTag = useCallback(async () => {
        if (!bulkMethod) { toast.error('Pick a method first'); return; }
        if (!rows.length) return;
        setBulkBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/transactions/bulk-tag`,
                { txn_ids: rows.map(r => r.id), method: bulkMethod },
                { withCredentials: true });
            const total = (data.updated_loads || 0) + (data.updated_cashouts || 0);
            toast.success(`Tagged ${total} ${kind === 'load' ? 'loads' : 'cashouts'} as ${bulkMethod}`);
            setRows([]);
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Bulk tag failed');
        } finally { setBulkBusy(false); }
    }, [bulkMethod, rows, kind, onDone]);

    const grandTotal = rows.reduce((s, r) => s + (r.amount || 0), 0);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#121212] border-white/10 max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
                data-testid="tag-unspecified-dialog">
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                        <Tag className="w-4 h-4 text-[#FFD700]" />
                        Tag Unspecified {kind === 'load' ? 'Loads' : 'Cashouts'}
                    </DialogTitle>
                    <DialogDescription className="text-[#A0AAB2]">
                        These {kind === 'load' ? 'loads' : 'cashouts'} were recorded without a method.
                        Assign one to keep the by-method breakdown accurate.
                    </DialogDescription>
                </DialogHeader>

                {/* Window selector */}
                <div className="flex items-center gap-2 text-xs" data-testid="tag-window-selector">
                    <span className="text-[#A0AAB2]">Window:</span>
                    {[1, 7, 30, 90].map(d => (
                        <button key={d}
                            type="button"
                            onClick={() => setDays(d)}
                            className={`px-2.5 py-1 rounded-md font-bold border transition ${
                                days === d
                                    ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                    : 'bg-[#0a0a0a] text-white border-white/10 hover:border-[#FFD700]/40'
                            }`}
                            data-testid={`tag-window-${d}`}>
                            {d === 1 ? 'Today' : `${d}d`}
                        </button>
                    ))}
                    <span className="ml-auto text-[#A0AAB2]">
                        {rows.length} entries · {fmtMoney(grandTotal)}
                    </span>
                </div>

                {/* Bulk-assign row */}
                {rows.length > 0 && (
                    <div className="border border-[#FFD700]/30 bg-[#FFD700]/5 rounded-lg p-3 space-y-2"
                        data-testid="bulk-tag-controls">
                        <p className="text-[11px] uppercase font-bold tracking-wide text-[#FFD700]">
                            Bulk-tag all {rows.length} visible
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5">
                            {METHODS.map(m => (
                                <button key={m} type="button"
                                    onClick={() => setBulkMethod(m)}
                                    className={`px-2 py-1 rounded text-[11px] font-bold border ${
                                        bulkMethod === m
                                            ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                            : 'bg-[#0a0a0a] text-white border-white/10 hover:border-[#FFD700]/40'
                                    }`}
                                    data-testid={`bulk-method-${m.toLowerCase().replace(/\s+/g, '-')}`}>
                                    {m}
                                </button>
                            ))}
                            <Button size="sm"
                                onClick={bulkTag}
                                disabled={!bulkMethod || bulkBusy}
                                className="ml-2 bg-[#FFD700] text-black hover:bg-[#FFD700]/90 h-7"
                                data-testid="bulk-tag-apply">
                                {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCheck className="w-3 h-3 mr-1" /> Apply</>}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Rows */}
                <div className="flex-1 overflow-y-auto -mx-2 px-2" data-testid="untagged-rows-scroller">
                    {loading ? (
                        <div className="flex items-center justify-center py-12 text-[#A0AAB2] text-xs">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="text-center py-12 text-[#A0AAB2] text-sm">
                            <CheckCheck className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                            All clear — no untagged {kind === 'load' ? 'loads' : 'cashouts'} in this window.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {rows.map(r => (
                                <div key={r.id}
                                    className="border border-white/10 rounded-md p-3 bg-[#0a0a0a]"
                                    data-testid={`untagged-row-${r.id}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="min-w-0">
                                            <p className="text-sm font-bold text-white truncate">{r.user_name}</p>
                                            <p className="text-[10px] text-[#A0AAB2] truncate">
                                                {fmtWhen(r.created_at)}
                                                {r.cashier_name ? ` · by ${r.cashier_name}` : ''}
                                                {r.operator_name ? ` · ${r.operator_name}` : ''}
                                                {' · '}{r.transaction_type}
                                            </p>
                                        </div>
                                        <span className="text-base font-mono font-bold text-[#39FF14]">{fmtMoney(r.amount)}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                        {METHODS.map(m => (
                                            <button key={m} type="button"
                                                onClick={() => tagOne(r.id, m)}
                                                disabled={!!rowBusy[r.id]}
                                                className="px-2 py-1 rounded text-[11px] font-bold border bg-[#121212] text-white border-white/10 hover:border-[#FFD700] hover:text-[#FFD700] disabled:opacity-50"
                                                data-testid={`row-${r.id}-tag-${m.toLowerCase().replace(/\s+/g, '-')}`}>
                                                {m}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}
                        className="border-white/10 text-white"
                        data-testid="tag-dialog-close">
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default TagUnspecifiedDialog;
